// import type { ChartOptions } from 'chart.js';
import dayjs from 'dayjs';
import { useI18n } from 'vue-i18n';
import isoWeek from 'dayjs/plugin/isoWeek';
import type { TooltipData } from '~/components/nrjx/chart/tooltip/index.vue';

import type { LineChartOptions } from '~/components/nrjx/chart/line/index.vue';

dayjs.extend(isoWeek);

export const useExploreLineChart = () => {
  // Composables
  const { t } = useI18n();
  const { formatNumberToIsoNumber } = useNumbers();
  const { getTailwindColor } = useTailwind();

  // Stores
  const { consumptionResponse, comparisonResponse, areasToCompare } = storeToRefs(chronologicalStore());
  const { areaId, aggregationLevel } = storeToRefs(exploreStore());
  const { getFormattedDateFromAggregateLevel } = useChart();
  const { getArea } = areaStore();

  const options = computed(
    (): LineChartOptions => ({
      height: 'h-[70vh]',
      unit: 'kW',
      tooltip: (index) => {
        const data = consumptionResponse.value?.items?.[index];
        if (!data) return undefined;

        if (areasToCompare.value.length > 0) {
          return comparisonTooltip({ index });
        }

        const tooltipData: TooltipData = {
          title: t('global.power'),
          subtitle: `${formatNumberToIsoNumber(data?.avg_power ?? 0, 2)} kW`,
          body: [
            { title: t('global.consumption'), value: `${formatNumberToIsoNumber(Number(data.consumption.quantity))} kWh` },
            {
              title: t('global.cost'),
              value: `${formatNumberToIsoNumber(Number(data.consumption.cost), Number(data.consumption.cost) < 10 ? 2 : 0)} €`,
            },
          ],
          footer: getFormattedDateFromAggregateLevel(data.start, aggregationLevel.value),
        };
        return tooltipData;
      },
    }),
  );

  // const options = ref<ChartLineOptions>({
  //   x: {
  //     ticks: {
  //       callback: (value: number) => {
  //         const data = consumptionResponse.value?.items[value];
  //         return data?.start ? getFormattedDateFromAggregateLevel(data.start, aggregationLevel.value) : '';
  //       },
  //     },
  //   },
  //   tooltip: (context) => {
  //     const index = context[0]?.index;
  //     if (!index) return null;
  //     const data = consumptionResponse.value?.items?.[index];
  //     if (!data) return null;

  //     if (areasToCompare.value.length > 0) {
  //       return comparisonTooltip({ index });
  //     }

  //     const tooltipData: TooltipData = {
  //       title: t('global.power'),
  //       subtitle: `${formatNumberToIsoNumber(data?.avg_power ?? 0, 2)} kW`,
  //       body: [
  //         { title: t('global.consumption'), value: `${formatNumberToIsoNumber(Number(data.consumption.quantity))} kWh` },
  //         {
  //           title: t('global.cost'),
  //           value: `${formatNumberToIsoNumber(Number(data.consumption.cost), Number(data.consumption.cost) < 10 ? 2 : 0)} €`,
  //         },
  //       ],
  //       footer: getFormattedDateFromAggregateLevel(data.start, aggregationLevel.value),
  //     };
  //     return tooltipData;
  //   },
  //   eventTooltip: (index) => {
  //     // Variables
  //     const data = productionResponse.value?.items[index];
  //     const area = areaId.value ? areaStore().getArea(areaId.value) : null;
  //     const start = dayjs(data?.production.start);
  //     const end = dayjs(data?.production.end);
  //     const date = start.isSame(end, 'day') ? start.format('DD/MM/YYYY') : `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;

  //     if (!data || !area) return undefined;

  //     return {
  //       title: t('global.epi'),
  //       subtitle: `${formatNumberToIsoNumber(Number(data.consumption_per_unit.quantity), 2)} kWh/${area?.production_unit?.symbol ?? '--'}`,
  //       body: [
  //         {
  //           title: t('global.quantity'),
  //           value: `${formatNumberToIsoNumber(Number(data.production.quantity))} ${area?.production_unit?.symbol ?? '--'}`,
  //         },
  //         { title: t('global.power'), value: `${formatNumberToIsoNumber(Number(data.avg_power), 2)} kW` },
  //       ],
  //       footer: date,
  //     };
  //   },
  // });

  const comparisonTooltip = (options: { index: number }) => {
    const item = comparisonResponse.value?.reference.items[options.index];
    const principalArea = getArea(areaId.value);

    return {
      body: [
        {
          title: principalArea?.name ?? 'Unknown Area',
          value: formatNumberToIsoNumber(item?.consumption.quantity ?? 0) + ' ' + 'kW',
          color: getTailwindColor('emerald-500'),
        },
        ...(comparisonResponse.value?.compared_to.map((compareArea) => {
          const area = getArea(compareArea.area_id);
          const color = areasToCompare.value.find((area) => area.id === compareArea.area_id)?.color;

          return {
            title: area?.name ?? 'Unknown Area',
            value: formatNumberToIsoNumber(compareArea.consumption.items[options.index]?.avg_power ?? 0) + ' ' + 'kW',
            color: color,
          };
        }) ?? []),
      ],
      footer: getFormattedDateFromAggregateLevel(item?.start ?? new Date(), aggregationLevel.value),
    };
  };

  return {
    options,
  };
};
